import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { PRIMARY_COLOR } from 'modules/Common/utils/brandColor';

// Shared ranked bar-list row for the Analytics "Top …" panels
// (TopCNCodesPanel + TopSuppliersPanel). Each row reads left→right:
//   leading label column · track bar sized by share · right-aligned value
//   (+ optional one-decimal percent) · chevron.
//
// The primary click is the button spanning the label→value cluster. An
// optional `secondaryAction` renders a second, sibling icon button on the
// right edge (kept OUTSIDE the primary button so the markup stays valid —
// no nested interactive elements) for panels that need a dual affordance,
// e.g. Top suppliers: primary = filter/scroll the in-page breakdown,
// secondary = deep-link to the supplier detail route.
//
// Without `onClick` the row is a plain <div>, NOT a disabled <button>:
// disabled form controls do not dispatch pointer events, which silently
// killed every `title` tooltip inside the row (segment titles, markTitle)
// on the read-only panels.
//
// `layout` picks the row geometry:
//   'inline' (default) → leading · track · value, all on one line. Needs a
//                        wide card; the track is what absorbs the squeeze.
//   'stacked'          → leading + value on line 1, full-width track on
//                        line 2. For narrow (1-of-3 column) panels, where
//                        an inline track collapses to its 40px floor.

const BAR_COLOR = PRIMARY_COLOR; // brand accent (whitelabel-aware)

// Shared "default factor" amber — the same value statusBarBits exports as
// GAP_COLOR. Inlined so this Common primitive does not depend on the CbamStock
// feature module.
const MARK_COLOR = '#d97706';

const clampPct = (pct) => Math.max(0, Math.min(100, pct));

const RankedBarRow = ({
  leading,
  leadingClassName = 'flex flex-col w-[140px] shrink-0',
  layout = 'inline',
  barPct,
  barColor = BAR_COLOR,
  // Optional stacked composition of the bar: [{pct, color, title}], each pct
  // in track units so the segments together span the same width barPct would.
  // Takes precedence over barPct/barColor when present.
  segments,
  // A vertical reference tick at `markPct` of the track — for rows that compare
  // a value against a benchmark (e.g. a product's intensity vs its default
  // factor). A tick, not a second bar: two stacked bars double the row height
  // and the comparison is a threshold, not a composition.
  markPct,
  markColor = MARK_COLOR,
  markTitle,
  valueLabel,
  valueSuffix,
  percentLabel,
  trailing,
  onClick,
  isActive,
  secondaryAction,
}) => {
  const track = (
    <div
      className={`relative h-2 bg-slate-100 rounded-full overflow-hidden ${
        layout === 'stacked' ? 'w-full' : 'flex-1 min-w-[40px]'
      }`}
    >
      {segments ? (
        <div className="flex h-full">
          {segments
            .filter((seg) => seg.pct > 0)
            .map((seg, i) => (
              <div
                key={i}
                className="h-full transition-all duration-300 ease-out"
                style={{
                  width: `${Math.min(100, seg.pct)}%`,
                  backgroundColor: seg.color,
                }}
                title={seg.title}
              />
            ))}
        </div>
      ) : (
        <div
          className="h-full transition-all duration-300 ease-out rounded-full"
          style={{ width: `${Math.max(2, Math.min(100, barPct))}%`, backgroundColor: barColor }}
        />
      )}
      {markPct != null && (
        <span
          aria-hidden="true"
          title={markTitle}
          className="absolute top-0 h-full w-0.5"
          // Shifted back by its own share of its width, so a tick at 100% sits inside the
          // track's right edge instead of being clipped by `overflow-hidden`.
          style={{
            left: `${clampPct(markPct)}%`,
            transform: `translateX(-${clampPct(markPct)}%)`,
            backgroundColor: markColor,
          }}
        />
      )}
    </div>
  );

  const value = (
    <div
      className={`shrink-0 text-right flex items-center justify-end gap-2 ${
        layout === 'stacked' ? '' : 'min-w-[72px]'
      }`}
    >
      <span className="text-xs font-semibold text-slate-900 tabular-nums">
        {valueLabel}
        {valueSuffix && (
          <span className="text-[10px] text-slate-400 font-normal ml-1">{valueSuffix}</span>
        )}
        {percentLabel && <span className="text-slate-400 font-normal"> · {percentLabel}</span>}
      </span>
      {trailing}
      {onClick && (
        <FontAwesomeIcon
          icon={faChevronRight}
          className="text-[10px] text-slate-500 group-hover:text-slate-700"
        />
      )}
    </div>
  );

  const bodyClassName = `flex-1 min-w-0 text-left px-2 py-2 rounded-lg transition-colors ${
    layout === 'stacked' ? 'flex flex-col gap-1.5' : 'flex items-center gap-3'
  } ${onClick ? 'hover:bg-slate-50 cursor-pointer' : 'cursor-default'} ${
    isActive ? 'bg-slate-50' : ''
  }`;

  const body =
    layout === 'stacked' ? (
      <>
        <div className="flex items-baseline gap-3">
          <div className={leadingClassName}>{leading}</div>
          {value}
        </div>
        {track}
      </>
    ) : (
      <>
        <div className={leadingClassName}>{leading}</div>
        {track}
        {value}
      </>
    );

  return (
    <div className="group flex items-stretch gap-1">
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          className={bodyClassName}
        >
          {body}
        </button>
      ) : (
        <div className={bodyClassName}>{body}</div>
      )}
      {secondaryAction && (
        <button
          type="button"
          onClick={secondaryAction.onClick}
          aria-label={secondaryAction.ariaLabel}
          title={secondaryAction.title || secondaryAction.ariaLabel}
          className="shrink-0 px-2 rounded-lg text-slate-500 hover:text-primaryColor hover:bg-slate-50 transition-colors"
        >
          <FontAwesomeIcon
            icon={secondaryAction.icon}
            className="text-[11px]"
          />
        </button>
      )}
    </div>
  );
};

export default RankedBarRow;
